import React, { memo, useCallback } from 'react';
import { CardViewX, CircularProgress, Typography, Icon } from '@ann/web-components';
import {
  DocumentsCardsContainer,
  EmptyState,
  LoadingContainer,
} from '../SlideoutDrives.styled';

const getFileIcon = (type, extension) => {
  const ext = extension?.toLowerCase() || type?.toLowerCase() || '';
  if (ext.includes('pdf')) return 'pictureAsPdf';
  if (ext.includes('doc') || ext.includes('word')) return 'description';
  if (ext.includes('xls') || ext.includes('excel')) return 'tableChart';
  if (ext.includes('ppt') || ext.includes('presentation')) return 'slideshow';
  if (ext.includes('image') || ext.includes('png') || ext.includes('jpg')) return 'image';
  return 'description';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const DocumentList = ({
  documents,
  loading,
  selectedDocumentIds,
  onDocumentSelect,
}) => {
  const handleCheckboxChange = useCallback((documentId) => {
    onDocumentSelect?.(documentId);
  }, [onDocumentSelect]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size="large" />
      </LoadingContainer>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <EmptyState>
        <Icon name="description" size="xl" />
        <Typography category="subtitle" size="medium" weight="bold">
          No documents found
        </Typography>
        <Typography category="body" size="medium" color="body">
          This drive doesn't have any documents.
        </Typography>
      </EmptyState>
    );
  }

  return (
    <DocumentsCardsContainer>
      {documents.map((doc) => {
        const documentId = doc.uuid || doc.id;
        const isSelected = selectedDocumentIds.has(documentId);
        const fileIcon = getFileIcon(doc.type, doc.extension);

        return (
          <CardViewX
            key={documentId}
            checked={isSelected}
            isCheckboxable
            onCheckboxChange={() => handleCheckboxChange(documentId)}
            showHover
            size="medium"
          >
            <CardViewX.Header
              icon={fileIcon}
              title={doc.name || 'Unnamed Document'}
              maxCharacters={40}
            />
            
            <CardViewX.Meta>
              {doc.size && (
                <CardViewX.MetaItem
                  icon="description"
                  label={formatFileSize(doc.size)}
                  tooltipContent="File size"
                />
              )}
              {doc.state && (
                <CardViewX.MetaItem
                  icon="info"
                  label={doc.state}
                  tooltipContent="Document state"
                />
              )}
            </CardViewX.Meta>
          </CardViewX>
        );
      })}
    </DocumentsCardsContainer>
  );
};

DocumentList.displayName = 'DocumentList';

export default memo(DocumentList);

